import { useEffect, useRef, useState } from 'react';
import BezierEasing from 'bezier-easing';
import { syncedNow, onTimesyncChange } from '@/utils/timesync';

// Server-synced coin flip — the coinflip counterpart of the jackpot wheel's
// RAF engine (see components/Jackpot/Game/WheelStage.tsx).
//
// Instead of a hard-coded CSS @keyframes spin + a separate setTimeout for the
// result (which can drift apart and, on a mid-flip mount, restarts from 0°),
// a SINGLE requestAnimationFrame loop reads the server-synced clock every frame
// and writes an inline `transform: rotateY(Xdeg)`. The rotation eases out from
// 0° to `endDeg` across the server's [flipStartAt, revealAt] window and lands
// EXACTLY on `endDeg` at `revealAt`. Because every client uses the same server
// timestamps + the same easing, the coin lands on the same face at the same
// wall-clock instant everywhere, and a late mount snaps straight to the correct
// in-flight angle (no replay, no desync).
const flipEasing = BezierEasing(0.1, 0.7, 0.05, 1);

// Used only when the backend didn't supply a flip window (e.g. opening an
// already-finished game from history) — a short local spin so the result still
// animates instead of popping in.
const FALLBACK_MS = 2800;

interface Options<T extends HTMLElement> {
    // The 3D-rotated coin element. The hook owns its `transform`/`animation`
    // while `active` is true.
    ref: React.RefObject<T | null>;
    // Absolute final rotation in degrees (full turns + landing offset).
    endDeg: number;
    // Drive the spin only while this is true (i.e. the "flipping" phase).
    active: boolean;
    // Server timestamps (ms, in synced-clock space). Missing → local fallback.
    flipStartAt?: number | null;
    revealAt?: number | null;
}

export function useSyncedFlip<T extends HTMLElement>({
    ref,
    endDeg,
    active,
    flipStartAt,
    revealAt,
}: Options<T>) {
    // Bumped when the timesync offset changes so the spin recomputes against
    // the corrected server clock (mirrors WheelStage's tsTick).
    const [tsTick, setTsTick] = useState(0);
    useEffect(() => onTimesyncChange(() => setTsTick((n) => n + 1)), []);

    // Cached spin window for the CURRENT flip. The effect re-runs whenever the
    // timesync offset changes (tsTick) — for a server-anchored spin that just
    // continues the same trajectory, but for the fallback window (no server
    // timestamps) we must NOT recompute `start` from a fresh syncedNow(), or
    // the coin restarts from 0° and the animation visibly plays twice (e.g. on
    // a page refresh, where timesync syncs mid-flip). Caching keeps the window
    // stable across re-runs; it resets when the flip ends (active → false).
    const windowRef = useRef<{ start: number; reveal: number } | null>(null);

    useEffect(() => {
        if (!active) {
            windowRef.current = null;
            return;
        }
        const el = ref.current;
        if (!el) return;

        let start: number;
        let reveal: number;
        if (flipStartAt != null && revealAt != null) {
            // Fully server-anchored — stable across re-runs by construction.
            start = flipStartAt;
            reveal = revealAt;
        } else if (windowRef.current) {
            // Reuse the window picked on the first run of this flip.
            ({ start, reveal } = windowRef.current);
        } else {
            // Derive a window once. Anchor to revealAt when we have it (synced
            // across clients); otherwise fall back to a local spin from now.
            reveal = revealAt ?? syncedNow() + FALLBACK_MS;
            start = revealAt != null ? revealAt - FALLBACK_MS : reveal - FALLBACK_MS;
            windowRef.current = { start, reveal };
        }
        const total = Math.max(1, reveal - start);

        // The RAF loop owns the transform — kill any CSS animation that could
        // fight it for the same property.
        el.style.animation = 'none';

        const rotationAt = (t: number) => {
            const p = Math.min(1, Math.max(0, (t - start) / total));
            return endDeg * flipEasing(p);
        };

        const land = () => {
            el.style.transform = `rotateY(${endDeg}deg)`;
        };

        const now0 = syncedNow();
        if (now0 >= reveal) {
            // Mounted after the reveal moment — settle instantly on the result.
            land();
            return;
        }
        el.style.transform = `rotateY(${rotationAt(now0)}deg)`;

        let rafId = 0;
        let cancelled = false;
        const tick = () => {
            if (cancelled) return;
            const t = syncedNow();
            if (t >= reveal) {
                land();
                return;
            }
            el.style.transform = `rotateY(${rotationAt(t)}deg)`;
            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);

        return () => {
            cancelled = true;
            cancelAnimationFrame(rafId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, endDeg, flipStartAt, revealAt, tsTick]);
}
